import * as React from 'react';

import { View } from '@longline/aqua-ui/controls/View';
import { DateTimeFormatter } from '@longline/aqua-ui/formatters/DateTimeFormatter';

import { useGlobalState } from '../../contexts/state/GlobalStateContext';
import { Pane } from '../../ui/Pane';
import { DeleteButton } from '../../contexts/confirm/DeleteButton';
import { useSpeciesContext } from './ISpeciesContext';
import { Grid } from '../../ui/Grid';
import { useAuth } from '../../contexts/auth/useAuth';
import { Can } from '../../ui/Can';

/**
 * A value with its unit appended, or nothing at all when the species has no
 * value for it -- a bare "g" would read as a measurement of zero.
 */
const withUnit = (value: number | null, unit: string) =>
  value === null || value === undefined ? null : `${value}${unit}`;

const SpeciesView = () => {
  const auth = useAuth();
  const ctx = useSpeciesContext();
  const globalState = useGlobalState();

  const [working, setWorking] = React.useState(false);

  // The mode === 'view' switch in SpeciesManager guarantees ctx.species is
  // populated before this component is rendered.
  if (!ctx.species) return null;
  const species = ctx.species;

  const deleteAction = <DeleteButton active={working} confirm title="Delete species" text="Are you sure you wish to delete this species?" onClick={async () => {
    setWorking(true);
    const result = await globalState.deleteSpecies(species);
    setWorking(false);
    if(result) ctx.setMode('list');
  }}/>

  return (
    <>
      <Pane.Heading>
        <Pane.Title>{species.name}</Pane.Title>
      </Pane.Heading>   
      <Pane.Subheader action={<Can permission="manage_species">{deleteAction}</Can>} expandable><Can permission="manage_species">DELETE SPECIES</Can></Pane.Subheader>

      <Pane.Body>
        <Grid>
          <Grid.Item weight={6}><View label="Name">{species.name}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Genus">{species.genus}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Species">{species.species}</View></Grid.Item>
          <Grid.Item weight={3}><View label="UK English">{species.nameUK}</View></Grid.Item>
          <Grid.Item weight={3}><View label="US English">{species.nameUS}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Norwegian">{species.nameNO}</View></Grid.Item>
          <Grid.Item weight={3}><View label="French">{species.nameFR}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Spanish">{species.nameES}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Italian">{species.nameIT}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Portuguese">{species.namePT}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Chinese">{species.nameZH}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Model code">{species.modelCode}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Initial weight">{withUnit(species.initialWeight, "g")}</View></Grid.Item>
          <Grid.Item weight={3}><View label="Culture period">{withUnit(species.culturePeriod, " days")}</View></Grid.Item>
          {/* Reference only: no growth model reads this. */}
          <Grid.Item weight={3}><View label="Harvest weight">{withUnit(species.harvestWeight, "g")}</View></Grid.Item>
          <Grid.Item weight={6}><View label="Created"><DateTimeFormatter type='longdatetime' value={species.created_at ?? ""}/></View></Grid.Item>
          <Grid.Item weight={6}><View label="Updated"><DateTimeFormatter type='longdatetime' value={species.updated_at ?? ""}/></View></Grid.Item>
        </Grid>
      </Pane.Body>

      <Pane.Footer>
        <Pane.CancelButton onClick={() => ctx.setMode('list')}/>
        <Can permission="manage_species">
          <Pane.EditButton onClick={() => ctx.setMode('edit')}/>
        </Can>
      </Pane.Footer>
    </>
  )
}

export { SpeciesView }
